import { type ProspectDto, type ProspectStage, useSalesGetStageHistory } from "@crowdcoursing/api/openapi";
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from "@mui/lab";
import { Box, CircularProgress, Typography } from "@mui/material";
import { skipToken } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { useSalesBubbleId } from "~hooks/useSalesBubbleId";
import { STAGES } from "~utils/constants/stages";

const tNoHistory = "No stage history available";

interface StageHistoryTimelineProps {
  prospect: ProspectDto;
}

export const StageHistoryTimeline = ({ prospect }: StageHistoryTimelineProps) => {
  const salesBubbleId = useSalesBubbleId();
  const { data: stageHistory, isLoading } = useSalesGetStageHistory(
    salesBubbleId && prospect.id ? { pathParams: { bubbleId: salesBubbleId, id: prospect.id } } : skipToken,
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress aria-label="Loading stage history" />
      </Box>
    );
  }

  if (!stageHistory?.length) {
    return <Typography color="text.secondary">{tNoHistory}</Typography>;
  }

  return (
    <Timeline
      position="right"
      sx={{
        [`& .MuiTimelineItem-root:before`]: {
          flex: 0,
          padding: 0,
        },
      }}
    >
      {stageHistory.map((history, index) => {
        const stage = history.toStage as ProspectStage;
        const stageInfo = STAGES[stage] ?? { emoji: "❓", name: "Unknown" };
        const isLast = index === stageHistory.length - 1;

        return (
          <TimelineItem
            key={history.id}
            sx={{
              minHeight: "70px",
            }}
          >
            <TimelineSeparator>
              <TimelineDot
                color={stage >= 5 ? (stage === 5 ? "success" : "error") : "primary"}
                sx={{ margin: "11px 0" }}
              >
                <Typography variant="caption">{stageInfo.emoji}</Typography>
              </TimelineDot>
              {!isLast && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ px: 2, py: "12px" }}>
              <Box>
                <Typography variant="subtitle2">{stageInfo.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {DateTime.fromISO(history.changedAt ?? "").toRelative()} by{" "}
                  {history.changedByUserId || history.changedByUserFirstName || history.changedByUserLastName
                    ? [history.changedByUserFirstName, history.changedByUserLastName].filter(Boolean).join(" ") ||
                      `User ${history.changedByUserId}`
                    : "System"}
                </Typography>
              </Box>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};
